import Link from 'next/link';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import clsxm from '@/lib/clsxm';
import { imageTools, ImageTool } from '@/constants/imageTools';
import { ArrowRightIcon } from '@heroicons/react/outline';

interface ImageToolsGridProps {
  tools?: ImageTool[];
}

const ImageToolsGrid = ({ tools: customTools }: ImageToolsGridProps) => {
  const { t } = useLocale('ai-images');

  const defaultTools = imageTools.map((tool) => ({
    ...tool,
    title: t(tool.title),
    description: t(tool.description),
  }));

  const tools = customTools || defaultTools;

  return (
    <div className='grid min-w-0 grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3 2xl:grid-cols-4'>
      {tools.map((tool) => (
        <Link
          key={tool.uri}
          href={tool.uri}
          className={clsxm(
            'group relative block h-52 sm:h-64 overflow-hidden rounded-xl bg-gray-100 dark:bg-slate-800',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:ring-offset-slate-900',
          )}
        >
          {/* Background Image/Video */}
          {tool.isVideoPreview ? (
            <video
              src={tool.image}
              autoPlay
              muted
              loop
              playsInline
              className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105'
            />
          ) : (
            <div className='absolute inset-0 h-full w-full'>
              <Image
                src={tool.image}
                alt={tool.title}
                fill
                className='object-cover transition-transform duration-500 group-hover:scale-105'
                sizes='(max-width: 640px) 50vw, (max-width: 1024px) 50vw, (max-width: 1536px) 33vw, 25vw'
              />
            </div>
          )}

          {/* Gradient Overlay - Base */}
          <div className='absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-80 transition-opacity duration-300 sm:from-black/60 sm:via-black/20 sm:opacity-60 group-hover:opacity-0' />

          {/* Gradient Overlay - Hover (Darker) */}
          <div className='absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100' />

          {/* Content */}
          {/* New Badge - Top Left */}
          {tool.isNew && (
            <span className='absolute top-3 left-3 z-20 inline-flex items-center rounded-full bg-primary-500/90 px-2 py-0.5 text-[10px] font-semibold text-white sm:top-4 sm:left-4 sm:px-2.5 sm:text-xs sm:backdrop-blur-sm'>
              {t('New')}
            </span>
          )}

          {/* Use Tool Button - Top Right (Visible on Hover) */}
          <div className='absolute top-3 right-3 z-20 hidden sm:block sm:top-4 sm:right-4 sm:opacity-0 sm:-translate-y-2 sm:transition-all sm:duration-300 sm:group-hover:opacity-100 sm:group-hover:translate-y-0'>
            <span className='inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/20 hover:bg-white/20 sm:backdrop-blur-md sm:px-4 sm:py-1.5 sm:text-sm'>
              {t('Use Tool')}
              <ArrowRightIcon className='ml-1.5 h-3.5 w-3.5' />
            </span>
          </div>

          <div className='absolute inset-0 flex flex-col justify-end p-4 sm:p-6'>
            <h3 className='text-base font-bold text-white transition-colors group-hover:text-primary-300 sm:text-xl'>
              {tool.title}
            </h3>

            <div className='hidden sm:block sm:mt-0 sm:max-h-0 sm:overflow-hidden sm:opacity-0 sm:transition-all sm:duration-300 sm:group-hover:max-h-24 sm:group-hover:opacity-100 sm:group-hover:mt-2'>
              <p className='text-xs text-gray-200 line-clamp-2 sm:text-sm'>
                {tool.description}
              </p>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};

export default ImageToolsGrid;
